/**
 * Paste dialog (spec §8.18): what is on the clipboard, the three modes, and why a mode is refused.
 */
import { useT } from "../../i18n";
import type { TreeID } from "loro-crdt";
import { useEffect, useMemo, useState } from "react";
import type { DocHandle } from "../../sync/docManager";
import { mirrorRefusal, mirrorSources, pasteBlocks, resolveMirrors, type PasteMode, type PasteTarget } from "./snapshot";
import { resolveSource } from "./sources";
import { countBlocks, type ClipboardEntry } from "./store";

export function PasteDialog({ entry, handle, target, userId, where, onClose, onPasted }: { entry: ClipboardEntry; handle: DocHandle; target: PasteTarget; userId: string; where: string; onClose(): void; onPasted?(roots: TreeID[]): void }) {
  const t = useT();
  const [mode, setMode] = useState<PasteMode>("copy");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [originalMissing, setOriginalMissing] = useState(false);
  const n = countBlocks(entry.blocks);
  const what = t(entry.page ? "paste.whatPage" : "paste.whatBlocks", { count: n, title: entry.sourcePageTitle || t("page.titlePlaceholder") });

  const sources = useMemo(() => mirrorSources(entry.blocks, entry.page ? { docId: entry.sourceDocId } : null), [entry]);
  const refusal = useMemo(() => mirrorRefusal(handle.doc, sources, target), [handle.doc, sources, target]);
  useEffect(() => {
    let alive = true;
    // every original must still be on its page
    void Promise.all(sources.map((s) => (s.kind === "page" ? Promise.resolve(true) : resolveSource(s).then((r) => r !== null)))).then((ok) => {
      if (alive) setOriginalMissing(ok.some((x) => !x));
    });
    return () => {
      alive = false;
    };
  }, [sources]);
  const mirrorBlocked = refusal ? t(refusal) : originalMissing ? t("paste.refuse.missing") : null;

  const paste = async () => {
    setBusy(true);
    setError(null);
    try {
      let roots: TreeID[];
      if (mode === "mirror") {
        roots = pasteBlocks(handle.doc, userId, target, "mirror", [], sources).roots;
      } else {
        const blocks = await resolveMirrors(entry.blocks, resolveSource);
        roots = pasteBlocks(handle.doc, userId, target, mode, blocks).roots;
      }
      onPasted?.(roots);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const option = (value: PasteMode, label: string, hint: string, disabled?: string | null) => (
    <label className={`flex cursor-pointer gap-2 rounded p-2 hover:bg-neutral-50 dark:hover:bg-neutral-800 ${disabled ? "cursor-not-allowed opacity-50" : ""}`}>
      <input type="radio" name="paste-mode" value={value} checked={mode === value} disabled={!!disabled} onChange={() => setMode(value)} data-testid={`paste-mode-${value}`} />
      <span>
        <span className="block font-medium">{label}</span>
        <span className="block text-xs text-neutral-500">{disabled ?? hint}</span>
      </span>
    </label>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div role="dialog" aria-label={t("paste.dialog")} className="w-full max-w-md rounded-lg border border-neutral-200 bg-[var(--surface)] p-5 text-sm shadow-[var(--shadow-lg)] dark:border-neutral-700" onClick={(e) => e.stopPropagation()} data-testid="paste-dialog">
        <h2 className="mb-1 text-base font-semibold">{t(where)}</h2>
        <p className="mb-3 text-neutral-600 dark:text-neutral-300" data-testid="paste-what">
          {what}
        </p>
        <div className="flex flex-col gap-1">
          {option("copy", t("paste.mode.copy"), t("paste.mode.copyHint"))}
          {option("mirror", t("paste.mode.mirror"), t("paste.mode.mirrorHint"), mirrorBlocked)}
          {option("empty", t("paste.mode.empty"), t("paste.mode.emptyHint"))}
        </div>
        {error && (
          <p className="mt-2 text-xs text-red-700" data-testid="paste-error">
            {error}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button className="rounded px-3 py-1 hover:bg-neutral-100 dark:hover:bg-neutral-800" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button className="rounded bg-accent px-3 py-1 text-white disabled:opacity-50" onClick={() => void paste()} disabled={busy || (mode === "mirror" && !!mirrorBlocked)} data-testid="paste-confirm">
            {busy ? t("paste.pasting") : t("paste.paste")}
          </button>
        </div>
      </div>
    </div>
  );
}
